'use client'

import {
    type AllHTMLAttributes,
    Fragment,
    type ReactNode,
    useCallback,
    useContext,
    useRef,
} from 'react'

import { Button } from '@/components/Button/Button'
import { ThemeContext } from '@/utils/themeContext'
import { X } from '@doist/icons/brand'
import {
    BaseField,
    type BaseFieldLabelVariant,
    type BaseFieldProps,
} from '../base-field/base-field'

type ValidInputType = 'text' | 'password' | 'email' | 'search' | 'number' | 'tel' | 'url'

type InputProps = AllHTMLAttributes<HTMLInputElement>

export type TextFieldWithActionProps = { action?: ReactNode; onClear?: never; clearLabel?: never }
export type TextFieldWithClearProps = { action?: never; onClear?: () => void; clearLabel?: string }

export type TextFieldProps = Omit<BaseFieldProps, 'action'> &
    BaseFieldLabelVariant & {
        value: NonNullable<InputProps['value']>
        type?: ValidInputType
        onChange: NonNullable<InputProps['onChange']>
        onBlur?: InputProps['onBlur']
        onFocus?: InputProps['onFocus']
        placeholder?: InputProps['placeholder']
    } & (TextFieldWithActionProps | TextFieldWithClearProps)

export function TextField({
    id,
    value,
    type = 'text',
    onChange,
    onBlur,
    onFocus,
    onClear,
    action,
    clearLabel = 'Clear',
    placeholder,
    ...restProps
}: TextFieldProps) {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const clearHandler = useCallback(() => {
        onClear?.()
        inputRef.current?.focus()
    }, [onClear])

    const { theme } = useContext(ThemeContext)

    const clearable = onClear && !restProps.disabled && value

    const ClearButton = (
        <Button
            onClick={clearHandler}
            startIcon={X}
            variant={theme === 'twist' ? 'quaternary' : 'secondary'}
            label={clearLabel}
        />
    )
    const actionable = action ? action : clearable ? ClearButton : undefined

    return (
        <BaseField
            {...restProps}
            id={id}
            value={value}
            {...(actionable ? { action: actionable } : {})}
        >
            {(fieldProps, icon, action) => {
                return (
                    <Fragment>
                        {icon}
                        <input
                            type={type}
                            value={value}
                            onChange={onChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            {...fieldProps}
                            ref={inputRef}
                        />
                        {action}
                    </Fragment>
                )
            }}
        </BaseField>
    )
}
